import { Button } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import taLogoD from "../assets/images/finchatGPT/taLogoD.png";
import taLogoL from "../assets/images/finchatGPT/taLogoL.png";
import {
  ChatWindowProps,
  Message,
  RootState,
} from "../assets/interfaces/interfaces";
import { useDispatch, useSelector } from "react-redux";
import FeedbackModal from "./FeedbackModal";
import ChatMessage from "./ChatMessage";
import SharePreviewModal from "./Share/SharePreviewModal";
import { useLocation, useParams } from "react-router-dom";
import ImageModal from "./ImageModal";
import { AuthActions } from "./Sidebar/constants";
import { AppDispatch } from "../redux/store";
import { openAuthModal } from "../redux/slices/userSlice";
import { makeUserMessage } from "./TradeGPT";
import { useMessages } from "../context/MessagesContext";
import { usePollPendingQuery } from "../hooks/usePollPendingQuery";
import { logHomepageView } from "../analytics";

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    console.log("Text copied to clipboard");
  }
  catch (err) {
    console.error("Failed to copy: ", err);
  }
};

// Component to display chat messages
const ChatWindow: React.FC<ChatWindowProps> = ({
  sessionId,
  messages,
  isUserSignedIn,
  shareModal,
  setShareModal,
  handleSendMessage,
  isAppendMessages,
}) => {
  const isDarkMode = useSelector((state: { theme }) => state.theme.darkMode);
  const finchatMessage = useSelector(
    (state: RootState) => state?.finchat?.finchatMessages?.[sessionId],
  );
  const { status: pastChatStatus } = useSelector(
    (state: RootState) => state.session,
  );
  const dispatch = useDispatch<AppDispatch>();
  // Feedback Modal States
  const { session_id } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState("");
  const [actionMessage, setActionMessage] = useState<Partial<Message> | null>();
  const handleCopy = (text: string) => {
    copyToClipboard(text);
  };

  const handleLike = (message: Partial<Message>) => {
    setActionMessage(message);
    setIsLiked(true);
    setModalOpen(true);
  };

  const handleDislike = (message: Partial<Message>) => {
    setActionMessage(message);
    setIsLiked(false);
    setModalOpen(true);
  };

  const location = useLocation();

  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const loading = finchatMessage?.outputLoading;
  const authLoading = useSelector((state: { auth }) => state.auth.loading);

  const scrollIntoView = useCallback(() => {
    queueMicrotask(() => {
      if (endOfMessagesRef.current) {
        endOfMessagesRef.current.scrollIntoView({ behavior: "smooth" });
      }
    });
  }, []);

  usePollPendingQuery(session_id);

  const scrollContainerRef = useRef(null);
  const lastScrolledToMessageIdRef = useRef<string | null>(null);
  const scrollToMessageId = location.state?.scrollToMessageId;
  const searchTerm = location.state?.searchedTerm;
  const pendingQueries = useSelector((state: RootState) => state.session.pendingQueries);
  // if there is a pending query, create a temp user message and append it to messages state for display
  useEffect(() => {
    const pendingQueryForSession = pendingQueries?.[sessionId];
    if (!pendingQueryForSession) return;
    setMessages((prev) => {
      const current = prev[sessionId] || [];
      const alreadyExists = current.some(msg => msg.chat_id === `temp${pendingQueryForSession.chat_id}`);
      if (alreadyExists) return prev;
      const newMessage = makeUserMessage(pendingQueryForSession.user_query, pendingQueryForSession.attachments, pendingQueryForSession.chat_id);
      return {
        ...prev,
        [session_id]: [...(prev[session_id] || []), newMessage],
      };
    });
  }, [pendingQueries, sessionId]);

  useEffect(() => {
    if (!messages) return;
    const isNewSearch = scrollToMessageId && scrollToMessageId !== lastScrolledToMessageIdRef.current;
    if (isNewSearch && !loading) {
      window.history.replaceState(null, "", location.pathname);
      return; // let child handle scroll
    }
    if (isAppendMessages) {
      // get new message to top of viewport
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.chat_id) {
        const newMsgEl = document.getElementById(`prompt-${lastMessage.chat_id}`);
        if (newMsgEl) {
          newMsgEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
    else {
      scrollIntoView(); // default scroll
    }
    lastScrolledToMessageIdRef.current = null;
  }, [messages, scrollToMessageId]);

  function onFeedbackModalClose() {
    setActionMessage(null);
    setModalOpen(false);
  }

  function onImageModalClose() {
    setIsImageModalOpen(false);
    setImageSrc("");
  }

  function handleImageClick(imageSrc) {
    setImageSrc(imageSrc);
    setIsImageModalOpen(true);
  }

  const { setMessages } = useMessages();

  useEffect(() => {
    if (isUserSignedIn && !authLoading && location.pathname.startsWith("/chat/")) {
      logHomepageView();
    }
  }, [isUserSignedIn, authLoading, location.pathname]);

  return (
    <div className="scroll-container flex-1 overflow-y-auto overflow-x-auto pt-2 z-20 relative custom-scrollbar box-content me-[6px]" ref={scrollContainerRef}>
      <SharePreviewModal session_id={session_id} open={shareModal} handleClose={() => setShareModal(false)} />
      <FeedbackModal isLiked={isLiked} open={modalOpen} onClose={onFeedbackModalClose} message={actionMessage} />
      <ImageModal open={isImageModalOpen} onClose={onImageModalClose} imageSrc={imageSrc} />

      {pastChatStatus !== "loading" && !authLoading && (
        <>
          {!messages?.length
            ? (
                <div className="flex h-full flex-col items-center justify-center">
                  <div className="relative">
                    <div className="mb-3 h-12 w-12">
                      <div className="relative flex h-full items-center justify-center rounded-full bg-white border border-token-border-medium">
                        <img
                          src={isDarkMode ? taLogoD : taLogoL}
                          alt="Logo"
                          className="portfolio-logo"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-medium text-token-text-primary">
                    How can I help you today?
                  </div>
                  {!isUserSignedIn && (
                    <Button
                      classes={{ root: "connect-portfolio-button" }}
                      onClick={() => dispatch(openAuthModal({ type: AuthActions.SIGNIN }))}
                    >
                      Log In
                    </Button>
                  )}
                </div>
              )
            : (
                <>
                  {messages.map((message, index) => (
                    /* Messages */
                    <ChatMessage
                      key={`${message.chat_id}_${index}`}
                      message={message}
                      onCopy={handleCopy}
                      onImageClick={handleImageClick}
                      onLike={() => { handleLike(message); }}
                      onDislike={() => { handleDislike(message); }}
                      showActions={index + 1 === messages.length && message.isBot}
                      handleSendMessage={handleSendMessage}
                      searchedTerm={message.chat_id === scrollToMessageId ? searchTerm : null}
                      triggerId={message.chat_id === scrollToMessageId ? scrollToMessageId : null}
                      onScrollHandled={() => {
                        lastScrolledToMessageIdRef.current = message.chat_id;
                      }}
                    />
                  ))}
                  {((loading
                    && !finchatMessage?.output.length) || pendingQueries[session_id]?.status
                  )
                  && (
                    <>
                      <ChatMessage message={finchatMessage?.output ?? ""} loading={true} onScrollRequest={scrollIntoView} />
                    </>
                  )}
                  <div ref={endOfMessagesRef} />
                </>
              )}
        </>
      )}
    </div>
  );
};

export default ChatWindow;
